import { Controller } from '@hotwired/stimulus'
import AirDatepicker from 'air-datepicker'
import localeEn from 'air-datepicker/locale/en'
import localeIt from 'air-datepicker/locale/it'
import localeEs from 'air-datepicker/locale/es'

import i18next from 'i18next'

// Vite's dep optimizer double-wraps these locale submodules' CJS `exports.default` (unlike the
// main `air-datepicker` UMD entry, which it unwraps correctly), so the import can come back as
// `{ default: { days: [...], ... } }` instead of the locale object itself. Without unwrapping
// here, air-datepicker's deep-merge of its own default options never overwrites the locale's
// `days`/`months`/etc. keys, and the picker silently falls back to its built-in Russian defaults.
const locales = {
  en: localeEn.default ?? localeEn,
  it: localeIt.default ?? localeIt,
  es: localeEs.default ?? localeEs
}

// Format a JS Date to 'YYYY-MM-DD' or 'YYYY-MM-DD HH:mm' based on timepicker state
// This is for display purposes only, the underlying value is always a full timestamp
function formatDateForDisplay(date, withTime = false) {
  const pad = (n) => n.toString().padStart(2, '0')
  const year = date.getFullYear()
  const month = pad(date.getMonth() + 1)
  const day = pad(date.getDate())
  let str = `${year}-${month}-${day}`

  if (withTime) {
    const hours = pad(date.getHours())
    const minutes = pad(date.getMinutes())
    str += ` ${hours}:${minutes}`
  }

  return str
}

// Parse an ISO 8601 string to a JS Date
function parseIsoDate(value) {
  if (!value) return null
  return new Date(value)
}

const todayButton = () => ({
  content: i18next.t('today', { ns: 'controllers__utils__form__datetime_picker_controller' }),
  onClick(dp) {
    const now = new Date();
    const withTime = dp.opts.timepicker
    dp.selectDate(now, { updateTime: withTime })
    dp.hide()
  }
})

const clearButton = () => ({
  content: '<i class="fa fa-times"></i>&nbsp; ' + i18next.t('clear', { ns: 'controllers__utils__form__datetime_picker_controller' }),
  onClick(dp) {
    dp.clear()
  }
})

const timeToggleButton = (isDatetimeElement) => ({
  content(dp) {
    const spanClass = dp.opts.timepicker ? 'text-success' : 'text-muted'
    return `<span class="${spanClass}"><i class="fa-solid fa-clock"></i>&nbsp; ${i18next.t('time', { ns: 'controllers__utils__form__datetime_picker_controller' })}</span>`
  },
  onClick(dp) {
    const hasTimepicker = !dp.opts.timepicker
    const input = dp.$el
    dp.update({ timepicker: hasTimepicker })

    if (isDatetimeElement) {
      isDatetimeElement.value = hasTimepicker
    }

    const selectedDate = dp.selectedDates[0]
    if (selectedDate) {
      input.value = formatDateForDisplay(selectedDate, hasTimepicker)
    }
  }
})

window.airDatepickerOptions = (isDatetimeElement, enableTime = true, allowClear = true) => {
  const buttons = [todayButton()]

  if (allowClear) buttons.push(clearButton())
  if (isDatetimeElement) buttons.push(timeToggleButton(isDatetimeElement))

  return {
    locale: locales[window.appData.locale] || localeIt,
    timepicker: enableTime,
    dateFormat: 'yyyy-MM-dd',
    timeFormat: 'HH:mm',
    buttons
  }
}

// Connects to data-controller="utils--form--datetime-picker"
export default class extends Controller {
  connect() {
    const isDatetimeElement = this.element.parentElement.querySelector('[data-is-datetime="true"]')
    const enableTime = isDatetimeElement ? isDatetimeElement.value === 'true' : this.element.dataset.enableTime === 'true'
    const allowClear = this.element.dataset.allowClear !== 'false'
    const defaultDate = this.element.dataset.defaultDate

    if (defaultDate && !this.element.value) {
      this.element.value = new Date().toISOString()
    }

    // Parse the incoming ISO string value
    const initialDate = parseIsoDate(this.element.value)

    const dp = new AirDatepicker(this.element, {
      ...window.airDatepickerOptions(isDatetimeElement, enableTime, allowClear),
      onSelect: ({ date }) => {
        // This handles the correct formatting for the display in the input
        if (date) {
          const withTime = dp.opts.timepicker;
          this.element.value = formatDateForDisplay(date, withTime)
        }
      }
    })

    // Set the initial date and update the display
    if (initialDate) {
      dp.selectDate(initialDate)
      this.element.value = formatDateForDisplay(initialDate, enableTime)
    }
  }
}
